import FormFieldComponent from '@/components/FormFieldComponent';
import FormFieldCalendar from '@/components/FormFieldCalendar';
import ToolBar from '@/components/ToolBar';
import { FormProvider, useForm } from 'react-hook-form';
import { useEffect, useRef, useState } from 'react';
import { CircleCheckBig, Flag, SquareCheckBig, SquarePen } from 'lucide-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  useHIInvoice,
  useHIWorkOrderByNumber,
  usePatchHIInvoiceMutation,
} from '@/services/homeInspectionService';
import { formatCurrency } from '@/lib/utils';
import Loader from '@/components/Loader';
import { NotFoundRedirectionWrapper } from '@/components/NotFoundRedirectWrapper';
import { InvoiceStatusBadge, invoiceStatus } from '@pages/caseManagement/invoice/InvoiceView';
import axios from 'axios';
import type { HIInvoiceStatus } from '@/types/homeInspection.types';
import { invoiceStatusBadgeDetails } from '@/utils/constant';
import { zodResolver } from '@hookform/resolvers/zod';
import { HiInvoiceFormSchema, type HiInvoiceFormSchemaType } from './hiInvoice.schema';
import { HiInvoiceBillingTable } from './HiInvoiceBillingTable';
import {
  buildHiInvoiceBillingPatch,
  hasHiInvoiceBillingChanges,
  mapInvoiceItemsToRows,
  type HiInvoiceBillingRow,
} from './hiInvoiceBillingPatch';
import { canApplyPaymentToHiInvoice, canConfirmHiInvoice, canEditHiInvoice } from './hiInvoicePermissions';
import { useUserStore } from '@/stores/user';
import AlertBox from '@/components/AlertBox';
import ApplyPaymentModal from '@/components/ApplyPaymentModal';
import { hasFeature } from '@/services/featureService';
import { toast } from '@/components/ui/use-toast';
import { displayError } from '@/lib/error';
import { buildHiInvoicePath } from './hiInvoiceRoute';

const getAxiosStatus = (error: unknown): number | undefined =>
  axios.isAxiosError(error) ? error.response?.status : undefined;

const statusBadge = (status: HIInvoiceStatus | undefined) => {
  if (!status) return invoiceStatus.DRAFT;
  return invoiceStatusBadgeDetails[status] ?? invoiceStatus.DRAFT;
};

const buildQuerySuffix = (workOrderId: string | null, workOrderNumber: string | null): string => {
  const params = new URLSearchParams();
  if (workOrderId) params.set('workOrderId', workOrderId);
  if (workOrderNumber) params.set('workOrderNumber', workOrderNumber);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
};

export const HiInvoiceView = ({ mode }: { mode: 'view' | 'edit' }) => {
  const navigate = useNavigate();
  const { id: invoiceNumber } = useParams();
  const [searchParams] = useSearchParams();
  // Required: invoice GET is by work order id (no get-by-invoice-number API).
  const workOrderId = searchParams.get('workOrderId');
  // Optional: enriches client/case fields and toolbar title.
  const workOrderNumber = searchParams.get('workOrderNumber');

  const form = useForm<HiInvoiceFormSchemaType>({
    mode: 'onBlur',
    resolver: zodResolver(HiInvoiceFormSchema),
  });
  const { control, reset, setValue } = form;
  const disabledStyleClass = '!border-[#CAC8C8] !bg-[#F5F5F5] !opacity-100';
  const user = useUserStore((state) => state.user);
  const patchInvoice = usePatchHIInvoiceMutation();
  const [billingItems, setBillingItems] = useState<HiInvoiceBillingRow[]>([]);
  const [isAlertBoxOpen, setIsAlertBoxOpen] = useState(false);
  const [openApplyPaymentModal, setOpenApplyPaymentModal] = useState(false);
  const hasApplyPaymentFeature = hasFeature('accounting.applyPayment');
  const lastHydratedInvoiceKeyRef = useRef<string | null>(null);

  const invoiceQuery = useHIInvoice(workOrderId);
  const workOrderQuery = useHIWorkOrderByNumber(workOrderNumber);

  const data = invoiceQuery.data;
  const workOrder = workOrderQuery.data;

  const isLoading =
    (!!workOrderId && invoiceQuery.isLoading) || (!!workOrderNumber && workOrderQuery.isLoading);

  const invoiceStatusCode = getAxiosStatus(invoiceQuery.error);
  const isNotFound =
    !workOrderId ||
    invoiceStatusCode === 404 ||
    (!invoiceQuery.isLoading && !!workOrderId && !invoiceQuery.isError && !data) ||
    (!!data && !!invoiceNumber && data.invoiceNumber !== invoiceNumber);

  const isGenericError =
    !!workOrderId &&
    invoiceQuery.isError &&
    invoiceStatusCode !== 404 &&
    !isNotFound;

  const workOrderContextFailed =
    Boolean(workOrderNumber) && workOrderQuery.isError && !workOrderQuery.isLoading;
  const contextUnavailable =
    Boolean(workOrderId) && !workOrderNumber && !workOrder && !invoiceQuery.isLoading && Boolean(data);

  const resolvedWorkOrderNumber = workOrder?.workOrderNumber ?? workOrderNumber;
  const toolbarTitle = resolvedWorkOrderNumber
    ? `W/O: ${resolvedWorkOrderNumber} - Invoice`
    : `Invoice: ${data?.invoiceNumber ?? invoiceNumber ?? '—'}`;

  const canEdit = canEditHiInvoice(user, data?.status);
  const canConfirm = canConfirmHiInvoice(user, data?.status);
  const canApplyPayment = hasApplyPaymentFeature && canApplyPaymentToHiInvoice(user, data?.status);
  const hasBillingChanges = data ? hasHiInvoiceBillingChanges(billingItems, data.billingItems) : false;

  // Hydrate identity fields whenever invoice/WO context arrives; only reset billing rows
  // when the invoice payload itself changes (avoids wiping in-progress edit on late WO load).
  useEffect(() => {
    if (!data) return;

    reset({
      invoiceId: data.invoiceNumber ?? '',
      clientName: workOrder?.clientName ?? '',
      caseName: workOrder?.caseName ?? '',
      toServe: workOrder?.ownerOrDefendantName ?? '',
      attorney: workOrder?.lawyer ?? '',
      contact: workOrder?.contactName ?? '',
      billingDate: data.billDate ? new Date(data.billDate) : undefined,
      amount: formatCurrency(data.totalAmount ?? 0),
    });

    const invoiceKey = `${data.id}:${data.updatedAt}`;
    if (lastHydratedInvoiceKeyRef.current !== invoiceKey) {
      setBillingItems(mapInvoiceItemsToRows(data.billingItems));
      lastHydratedInvoiceKeyRef.current = invoiceKey;
    }
  }, [data, workOrder, reset]);

  useEffect(() => {
    const previewTotal = billingItems.reduce((acc, item) => acc + item.lineTotal, 0);
    setValue('amount', formatCurrency(billingItems.length ? previewTotal : 0));
  }, [billingItems, setValue]);

  useEffect(() => {
    if (
      mode === 'edit' &&
      !isLoading &&
      data &&
      data.status !== 'DRAFT' &&
      data.status !== 'REVIEWED' &&
      invoiceNumber
    ) {
      navigate(
        `/home-inspection/invoice/${encodeURIComponent(invoiceNumber)}${buildQuerySuffix(workOrderId, workOrderNumber)}`,
        { replace: true }
      );
    }
  }, [mode, isLoading, data, invoiceNumber, workOrderId, workOrderNumber, navigate]);

  const saveBillingHandler = () => {
    if (!data) return;
    const billingItemsPatch = buildHiInvoiceBillingPatch(billingItems, data.billingItems);
    const hasPatch =
      (billingItemsPatch.create?.length ?? 0) > 0 ||
      (billingItemsPatch.update?.length ?? 0) > 0 ||
      (billingItemsPatch.delete?.length ?? 0) > 0;
    if (!hasPatch) {
      return;
    }

    // PATCH /home-inspection/invoice/{id} looks up by invoice number (not UUID).
    patchInvoice.mutate(
      {
        invoiceId: data.invoiceNumber,
        workOrderId,
        body: { billingItems: billingItemsPatch },
      },
      {
        onSuccess: (response) => {
          toast({ description: response.message || 'Invoice updated successfully' });
          setIsAlertBoxOpen(false);
          navigate(
            `/home-inspection/invoice/${encodeURIComponent(data.invoiceNumber)}${buildQuerySuffix(workOrderId, workOrderNumber)}`
          );
        },
        onError: (error) => {
          displayError(error || 'Failed to update invoice');
        },
      }
    );
  };

  const confirmHandler = () => {
    if (!data) return;
    patchInvoice.mutate(
      {
        invoiceId: data.invoiceNumber,
        workOrderId,
        body: { status: 'CONFIRMED' },
      },
      {
        onSuccess: (response) => {
          toast({ description: response.message || 'Invoice confirmed successfully' });
          void invoiceQuery.refetch();
        },
        onError: (error) => {
          displayError(error || 'Failed to confirm invoice');
        },
      }
    );
  };

  const markAsPaidHandler = () => {
    if (!data) return;
    patchInvoice.mutate(
      {
        invoiceId: data.invoiceNumber,
        workOrderId,
        body: { status: 'PAID' },
      },
      {
        onSuccess: (response) => {
          toast({ description: response.message || 'Invoice updated successfully' });
          void invoiceQuery.refetch();
        },
        onError: (error) => {
          displayError(error || 'Failed to update invoice');
        },
      }
    );
  };

  if (isGenericError) {
    return (
      <div className="flex items-center justify-center h-screen flex-col gap-8">
        <h1 className="text-[32px] font-normal text-black text-center">
          Failed to load Home Inspection invoice
        </h1>
        <p className="text-placeholder text-sm text-center max-w-md">
          Something went wrong while loading this invoice. Please try again or go back.
        </p>
        <Button variant="default" className="w-fit px-4 cursor-pointer" onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <NotFoundRedirectionWrapper itemName="Invoice" isItemNotFound={isNotFound}>
      <Loader className="w-full scrollbar-hide flex gap-6 flex-col" isLoading={isLoading}>
        <div className="w-full flex flex-row gap-2">
          {workOrder?.rushType && workOrder.rushType !== 'NONE' && (
            <div className="text-error h-5 w-5 pt-2" title="Rush Case">
              <Flag className="text-[#D72222] w-4 h-4" fill="#D72222" />
            </div>
          )}
          <ToolBar
            title={toolbarTitle}
            secondaryTitle={
              <InvoiceStatusBadge key={data?.status} status={statusBadge(data?.status)} />
            }
            additionalButton={
              <div className="flex flex-row gap-6 items-center">
                {mode === 'view' && (
                  <>
                    {canEdit && (
                      <div
                        key="edit"
                        className="border-[1px] border-[#CAC8C8] p-1 h-[32px] w-[32px] rounded-md cursor-pointer"
                        onClick={() =>
                          navigate(
                            `update${buildQuerySuffix(workOrderId, workOrderNumber)}`
                          )
                        }
                        aria-label="Edit invoice"
                      >
                        <SquarePen className="text-placeholder" />
                      </div>
                    )}
                    {canConfirm && (
                      <Loader isLoading={patchInvoice.isPending}>
                        <Button variant="outline" className="h-[36px]" onClick={confirmHandler}>
                          <div className="flex flex-row gap-3 items-center">
                            <SquareCheckBig className="h-4 w-4" />
                            Mark as Confirmed
                          </div>
                        </Button>
                      </Loader>
                    )}
                    {canApplyPayment && (
                      <Button
                        className="h-[36px] bg-[#1B8652] hover:bg-[#1B8652]"
                        onClick={() => setOpenApplyPaymentModal(true)}
                      >
                        <div className="flex flex-row gap-3 items-center">
                          <CircleCheckBig className="h-4 w-4" />
                          Apply Payment
                        </div>
                      </Button>
                    )}
                  </>
                )}
              </div>
            }
          />
        </div>

        {workOrderContextFailed && (
          <p className="text-sm text-placeholder" role="status">
            Work order details could not be loaded. Invoice billing data is still shown below.
          </p>
        )}
        {contextUnavailable && (
          <p className="text-sm text-placeholder" role="status">
            Client and case details are unavailable for this link. Invoice billing data is shown below.
          </p>
        )}

        <FormProvider {...form}>
          <div className="grid grid-cols-12 gap-6 w-full">
            <div className="col-span-12 md:col-span-6 space-y-4">
              <FormFieldComponent
                name="invoiceId"
                control={control}
                disabled
                label="Invoice #"
                disabledStyle={disabledStyleClass}
              />
              <FormFieldComponent
                name="clientName"
                control={control}
                disabled
                label="Client Name"
                disabledStyle={disabledStyleClass}
              />
              <FormFieldComponent
                name="caseName"
                control={control}
                disabled
                label="Case Name"
                disabledStyle={disabledStyleClass}
              />
              <FormFieldComponent
                name="toServe"
                control={control}
                disabled
                label="Owner / Defendant"
                disabledStyle={disabledStyleClass}
              />
              <FormFieldComponent
                name="attorney"
                control={control}
                disabled
                label="Attorney Name"
                disabledStyle={disabledStyleClass}
              />
              <FormFieldComponent
                name="contact"
                control={control}
                disabled
                label="Contact Name"
                disabledStyle={disabledStyleClass}
              />
            </div>

            <div className="col-span-12 md:col-span-6 border-[1px] border-[#CAC8C8] h-full rounded-md">
              <div className="bg-[#f5f5f5] rounded-md p-6 font-bold">Billing Information</div>
              <HiInvoiceBillingTable
                mode={mode}
                billingItems={billingItems}
                setBillingItems={setBillingItems}
              />
            </div>

            <div className="col-span-12 md:col-span-6 space-y-4">
              <FormFieldCalendar
                name="billingDate"
                control={control}
                label="Billing Date"
                disabled
                disabledStyle={disabledStyleClass}
                placeholder="Invoice not confirmed"
              />
            </div>
            <div className="col-span-12 md:col-span-6 space-y-4">
              <FormFieldComponent
                name="amount"
                control={control}
                disabled
                label="Total Billing Amount"
                disabledStyle={disabledStyleClass}
              />
            </div>
          </div>
        </FormProvider>

        {openApplyPaymentModal && (
          <ApplyPaymentModal
            open={openApplyPaymentModal}
            onOpenChange={setOpenApplyPaymentModal}
            invoices={
              data?.invoiceNumber
                ? [
                    {
                      invoiceNumber: data.invoiceNumber,
                      invoiceAmount: data.totalAmount ?? 0,
                      balance: data.totalAmount ?? 0,
                    },
                  ]
                : []
            }
            onSuccess={(paymentData) => {
              setOpenApplyPaymentModal(false);
              const remainingBalance = paymentData.invoices[0]?.balance ?? 0;
              if (remainingBalance <= 0) {
                markAsPaidHandler();
              } else {
                void invoiceQuery.refetch();
              }
            }}
          />
        )}

        {mode === 'edit' && (
          <div className="flex flex-row gap-3 w-full justify-end pb-6 pt-10">
            <Button
              variant="outline"
              className="h-[36px] w-[112px]"
              onClick={() => {
                if (data && workOrderId) {
                  navigate(
                    buildHiInvoicePath(
                      data.invoiceNumber,
                      workOrderId,
                      workOrderNumber ?? undefined
                    )
                  );
                  return;
                }
                navigate(-1);
              }}
            >
              Cancel
            </Button>
            <AlertBox
              triggerButton={
                <Button
                  variant="default"
                  className="h-[36px] w-[103px] hover:bg-[#12375C]"
                  disabled={!hasBillingChanges || patchInvoice.isPending}
                  onClick={() => setIsAlertBoxOpen(true)}
                  data-testid="trigger-save-button"
                >
                  Save
                </Button>
              }
              title="Are you sure?"
              description="Would you like to update invoice details?"
              cancelBtnLabel="No"
              confirmBtnLabel="Yes"
              open={isAlertBoxOpen}
              isLoading={patchInvoice.isPending}
              cancelBtnClick={() => setIsAlertBoxOpen(false)}
              confirmBtnClick={saveBillingHandler}
            />
          </div>
        )}
      </Loader>
    </NotFoundRedirectionWrapper>
  );
};
